import { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { BUY_ME_A_COFFEE_URL } from '../../shared/config/constants';
import { serializeBlocksToText } from '../apps/cidr-planner/bulkImport';
import { coveragePercent, normalizeCidr, validateCidrInput, filterBlocksForIpCountMode, parseCidr } from '../apps/cidr-planner/cidrUtils';
import { isAwsSubnetAllowed } from '../apps/cidr-planner/usableIpUtils';
import type { AddressRange, CidrBlock, IpCountMode, PlannerState } from '../apps/cidr-planner/types';
import { DEFAULT_PLANNER_STATE } from '../apps/cidr-planner/types';
import { AddCidrDialog } from '../components/cidr-planner/AddCidrDialog';
import { AwsModeConfirmDialog } from '../components/cidr-planner/AwsModeConfirmDialog';
import { ConfirmDialog } from '../components/cidr-planner/ConfirmDialog';
import { BlockActionDialog } from '../components/cidr-planner/BlockActionDialog';
import { BlockDetailSheet } from '../components/cidr-planner/BlockDetailSheet';
import { BlockSplitDialog } from '../components/cidr-planner/BlockSplitDialog';
import { CidrCanvas } from '../components/cidr-planner/CidrCanvas';
import { CidrSheet } from '../components/cidr-planner/CidrSheet';
import { GapActionDialog } from '../components/cidr-planner/GapActionDialog';
import { ImportCidrDialog } from '../components/cidr-planner/ImportCidrDialog';
import { ModeToggle } from '../components/cidr-planner/ModeToggle';
import { getLargestCidrForGap } from '../apps/cidr-planner/splitPlanner';
import { SplitDialog } from '../components/cidr-planner/SplitDialog';
import { SubnetList } from '../components/cidr-planner/SubnetList';
import './AppsCidrPlanner.css';

type DialogState =
  | { type: 'gap'; gap: AddressRange }
  | { type: 'gap-add'; gap: AddressRange }
  | { type: 'split'; gap: AddressRange }
  | { type: 'add' }
  | { type: 'import' }
  | { type: 'block'; block: CidrBlock }
  | { type: 'block-edit'; block: CidrBlock }
  | { type: 'block-split'; block: CidrBlock }
  | { type: 'aws-mode-confirm'; blocksToRemove: CidrBlock[]; nextBlocks: CidrBlock[] }
  | { type: 'confirm-reset' }
  | { type: 'confirm-parent'; nextCidr: string }
  | { type: 'confirm-delete-block'; block: CidrBlock; resume?: 'block-edit' }
  | null;

const TEXT = {
  ja: {
    title: 'CIDRプランナー',
    parent: '親 CIDR',
    mobileTabs: '表示',
    mobilePreview: 'プレビュー',
    mobileList: (count: number) => `一覧${count > 0 ? ` (${count})` : ''}`,
    importCidr: 'テキスト編集',
    reset: 'すべてクリア',
    resetTitle: 'すべてクリア',
    resetConfirm: 'すべてのブロックを削除し、初期状態に戻します。',
    parentChangeTitle: '親 CIDR を変更',
    parentConfirm: '親 CIDR を変更すると、すべてのブロックが削除されます。',
    confirmReset: 'クリア',
    confirmParent: '変更する',
    deleteBlockTitle: 'ブロックを削除',
    deleteBlockConfirm: (cidr: string, name: string) =>
      name ? `${cidr}（${name}）を削除します。` : `${cidr} を削除します。`,
    confirmDelete: '削除',
    cancel: 'キャンセル',
    close: '閉じる',
    invalidParent: '親 CIDR の形式が正しくありません',
    summary: (used: number, free: number) => `使用 ${used.toFixed(2)}% / 空き ${free.toFixed(2)}%`,
    modeStandard: '標準',
    modeAws: 'AWS VPC',
    mobileSettings: '設定',
    intro: 'CIDRの分割と空き具合を確認できます。',
    description: 'CIDRの分割と空き具合を確認できるツール。',
    buyMeACoffee: 'お役に立てたら、投げ銭で応援してもらえるとうれしいです',
  },
  en: {
    title: 'CIDR Planner',
    parent: 'Parent CIDR',
    mobileTabs: 'View',
    mobilePreview: 'Preview',
    mobileList: (count: number) => `List${count > 0 ? ` (${count})` : ''}`,
    importCidr: 'Edit text',
    reset: 'Clear all',
    resetTitle: 'Clear all',
    resetConfirm: 'All blocks will be removed and the planner will reset.',
    parentChangeTitle: 'Change parent CIDR',
    parentConfirm: 'Changing the parent CIDR will remove all blocks.',
    confirmReset: 'Clear',
    confirmParent: 'Change',
    deleteBlockTitle: 'Delete block',
    deleteBlockConfirm: (cidr: string, name: string) =>
      name ? `Delete ${cidr} (${name})?` : `Delete ${cidr}?`,
    confirmDelete: 'Delete',
    cancel: 'Cancel',
    close: 'Close',
    invalidParent: 'Invalid parent CIDR format',
    summary: (used: number, free: number) => `Used ${used.toFixed(2)}% / Free ${free.toFixed(2)}%`,
    modeStandard: 'Standard',
    modeAws: 'AWS VPC',
    mobileSettings: 'Settings',
    intro: 'Check CIDR splits and free space.',
    description: 'Check CIDR splits and free space.',
    buyMeACoffee: 'If this tool helped, you can buy me a coffee.',
  },
} as const;

function createBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function AppsCidrPlanner() {
  const lang = useLanguage();
  const t = TEXT[lang];
  const siteUrl = getSiteUrl();
  const pageUrl = `${siteUrl}/${lang}/apps/cidr-planner`;

  const [state, setState] = useState<PlannerState>(DEFAULT_PLANNER_STATE);
  const [parentInput, setParentInput] = useState(DEFAULT_PLANNER_STATE.parentCidr);
  const [parentError, setParentError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [mobilePanel, setMobilePanel] = useState<'canvas' | 'list'>('canvas');
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);

  useMetaTags({
    title: `${t.title} | Version0.1`,
    description: t.description,
    ogp: {
      title: `${t.title} | Version0.1`,
      description: t.description,
      url: pageUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary',
      title: `${t.title} | Version0.1`,
      description: t.description,
      image: '/og-image.png',
    },
    canonicalUrl: pageUrl,
  });

  const coverage = useMemo(
    () => coveragePercent(state.parentCidr, state.blocks.map((block) => block.cidr)),
    [state.parentCidr, state.blocks],
  );

  const existingCidrs = useMemo(
    () => state.blocks.map((block) => block.cidr),
    [state.blocks],
  );

  const applyParentCidr = useCallback(
    (value: string) => {
      const validation = validateCidrInput(value);
      if (!validation.ok) {
        setParentError(t.invalidParent);
        return false;
      }

      setParentError(null);
      setState((current) => ({
        ...current,
        parentCidr: validation.normalized,
        blocks: [],
        blocksText: null,
      }));
      setParentInput(validation.normalized);
      return true;
    },
    [t.invalidParent],
  );

  const handleParentBlur = useCallback(() => {
    if (parentInput.trim() === state.parentCidr) {
      setParentError(null);
      return;
    }

    const validation = validateCidrInput(parentInput);
    if (!validation.ok) {
      setParentError(t.invalidParent);
      return;
    }

    if (validation.normalized === state.parentCidr) {
      setParentInput(validation.normalized);
      setParentError(null);
      return;
    }

    if (state.blocks.length > 0) {
      setDialog({ type: 'confirm-parent', nextCidr: validation.normalized });
      return;
    }

    applyParentCidr(validation.normalized);
  }, [applyParentCidr, parentInput, state.blocks.length, state.parentCidr, t.invalidParent]);

  const performReset = useCallback(() => {
    setState(DEFAULT_PLANNER_STATE);
    setParentInput(DEFAULT_PLANNER_STATE.parentCidr);
    setParentError(null);
    setDialog(null);
  }, []);

  const handleReset = useCallback(() => {
    if (state.blocks.length === 0) {
      performReset();
      return;
    }
    setDialog({ type: 'confirm-reset' });
  }, [performReset, state.blocks.length]);

  const handleConfirmParentChange = useCallback(() => {
    if (dialog?.type !== 'confirm-parent') {
      return;
    }
    applyParentCidr(dialog.nextCidr);
    setDialog(null);
  }, [applyParentCidr, dialog]);

  const handleCancelParentChange = useCallback(() => {
    setParentInput(state.parentCidr);
    setDialog(null);
  }, [state.parentCidr]);

  const addBlock = useCallback((payload: { cidr: string; name: string }) => {
    setState((current) => ({
      ...current,
      blocksText: null,
      blocks: [
        ...current.blocks,
        {
          id: createBlockId(),
          cidr: normalizeCidr(payload.cidr),
          name: payload.name,
        },
      ],
    }));
    setDialog(null);
  }, []);

  const addBlocks = useCallback((payloads: Array<{ cidr: string; name: string }>) => {
    setState((current) => ({
      ...current,
      blocksText: null,
      blocks: [
        ...current.blocks,
        ...payloads.map((payload) => ({
          id: createBlockId(),
          cidr: normalizeCidr(payload.cidr),
          name: payload.name,
        })),
      ],
    }));
    setDialog(null);
  }, []);

  const replaceBlocksFromText = useCallback(
    (result: { text: string; payloads: Array<{ cidr: string; name: string }> }) => {
      setState((current) => ({
        ...current,
        blocksText: result.text,
        blocks: result.payloads.map((payload) => ({
          id: createBlockId(),
          cidr: normalizeCidr(payload.cidr),
          name: payload.name,
        })),
      }));
      setDialog(null);
    },
    [],
  );

  const splitBlock = useCallback((blockId: string, payloads: Array<{ cidr: string; name: string }>) => {
    setState((current) => ({
      ...current,
      blocksText: null,
      blocks: [
        ...current.blocks.filter((block) => block.id !== blockId),
        ...payloads.map((payload) => ({
          id: createBlockId(),
          cidr: normalizeCidr(payload.cidr),
          name: payload.name,
        })),
      ],
    }));
    setDialog(null);
  }, []);

  const updateBlock = useCallback((blockId: string, payload: { cidr: string; name: string }) => {
    setState((current) => ({
      ...current,
      blocksText: null,
      blocks: current.blocks.map((block) =>
        block.id === blockId
          ? { ...block, cidr: normalizeCidr(payload.cidr), name: payload.name }
          : block,
      ),
    }));
    setDialog(null);
  }, []);

  const deleteBlock = useCallback((blockId: string) => {
    setState((current) => ({
      ...current,
      blocksText: null,
      blocks: current.blocks.filter((block) => block.id !== blockId),
    }));
    setDialog(null);
  }, []);

  const requestDeleteBlock = useCallback((block: CidrBlock, resume?: 'block-edit') => {
    setDialog({ type: 'confirm-delete-block', block, resume });
  }, []);

  const handleCancelDeleteBlock = useCallback(() => {
    setDialog((current) => {
      if (current?.type === 'confirm-delete-block' && current.resume === 'block-edit') {
        return { type: 'block-edit', block: current.block };
      }
      return null;
    });
  }, []);

  const applyIpCountMode = useCallback((mode: IpCountMode, blocks: CidrBlock[]) => {
    setState((current) => ({ ...current, ipCountMode: mode, blocks, blocksText: null }));
    setDialog((current) => {
      if (current?.type === 'aws-mode-confirm') {
        return null;
      }
      if (mode !== 'aws') {
        return current;
      }
      if (
        current?.type === 'block' ||
        current?.type === 'block-edit' ||
        current?.type === 'block-split'
      ) {
        const { prefix } = parseCidr(current.block.cidr);
        return isAwsSubnetAllowed(prefix, 'aws') ? current : null;
      }
      return current;
    });
  }, []);

  const handleModeChange = useCallback(
    (mode: IpCountMode) => {
      if (mode === state.ipCountMode) {
        return;
      }

      const nextBlocks = filterBlocksForIpCountMode(state.blocks, mode);
      const blocksToRemove = state.blocks.filter(
        (block) => !nextBlocks.some((nextBlock) => nextBlock.id === block.id),
      );

      if (mode === 'aws' && blocksToRemove.length > 0) {
        setDialog({ type: 'aws-mode-confirm', blocksToRemove, nextBlocks });
        return;
      }

      applyIpCountMode(mode, nextBlocks);
    },
    [applyIpCountMode, state.blocks, state.ipCountMode],
  );

  const canvasPanelClassName = `apps-cidr-planner-page__canvas-section${
    mobilePanel !== 'canvas' ? ' apps-cidr-planner-page__panel--hidden-mobile' : ''
  }`;
  const sidebarClassName = `apps-cidr-planner-page__sidebar${
    mobilePanel !== 'list' ? ' apps-cidr-planner-page__panel--hidden-mobile' : ''
  }`;

  const settingsFields = (
    <>
      <label className="apps-cidr-planner-page__field apps-cidr-planner-page__field--cidr">
        <span className="apps-cidr-planner-page__label">{t.parent}</span>
        <input
          className="apps-cidr-planner-page__input"
          value={parentInput}
          onChange={(event) => setParentInput(event.target.value)}
          onBlur={handleParentBlur}
          inputMode="decimal"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      <div className="apps-cidr-planner-page__field apps-cidr-planner-page__field--mode">
        <ModeToggle mode={state.ipCountMode} onChange={handleModeChange} lang={lang} />
      </div>
      {parentError && <p className="apps-cidr-planner-page__error">{parentError}</p>}
    </>
  );

  return (
    <main className="apps-cidr-planner-page">
      <div className="apps-cidr-planner-page__container">
        <div className="apps-cidr-planner-page__header">
          <h1 className="apps-cidr-planner-page__title">{t.title}</h1>
          <button
            type="button"
            className="apps-cidr-planner-page__settings-btn"
            aria-label={t.mobileSettings}
            onClick={() => setMobileSettingsOpen(true)}
          >
            <svg
              className="apps-cidr-planner-page__settings-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </div>
        <p className="apps-cidr-planner-page__description">{t.intro}</p>

        <div className="apps-cidr-planner-page__workspace">
          <div className="apps-cidr-planner-page__mobile-tabs" role="tablist" aria-label={t.mobileTabs}>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePanel === 'canvas'}
              className={`apps-cidr-planner-page__mobile-tab${
                mobilePanel === 'canvas' ? ' apps-cidr-planner-page__mobile-tab--active' : ''
              }`}
              onClick={() => setMobilePanel('canvas')}
            >
              {t.mobilePreview}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePanel === 'list'}
              className={`apps-cidr-planner-page__mobile-tab${
                mobilePanel === 'list' ? ' apps-cidr-planner-page__mobile-tab--active' : ''
              }`}
              onClick={() => setMobilePanel('list')}
            >
              {t.mobileList(state.blocks.length)}
            </button>
          </div>

          <section className={canvasPanelClassName}>
            <CidrCanvas
              parentCidr={state.parentCidr}
              blocks={state.blocks}
              lang={lang}
              onGapClick={(gap) => setDialog({ type: 'gap', gap })}
              onBlockClick={(block) => setDialog({ type: 'block', block })}
            />
          </section>

          <aside className={sidebarClassName}>
            <p className="apps-cidr-planner-page__summary">
              {t.summary(coverage.used, coverage.free)}
            </p>
            <SubnetList
              blocks={state.blocks}
              ipCountMode={state.ipCountMode}
              lang={lang}
              actionLabel={t.importCidr}
              onAction={() => setDialog({ type: 'import' })}
              onSelect={(block) => setDialog({ type: 'block', block })}
              onDelete={(blockId) => {
                const block = state.blocks.find((item) => item.id === blockId);
                if (block) {
                  requestDeleteBlock(block);
                }
              }}
            />
          </aside>
        </div>

        <section className="apps-cidr-planner-page__controls apps-cidr-planner-page__controls--desktop">
          <div className="apps-cidr-planner-page__toolbar">
            {settingsFields}
            <div className="apps-cidr-planner-page__actions">
              <button
                type="button"
                className="apps-cidr-planner-page__btn apps-cidr-planner-page__btn--danger"
                onClick={handleReset}
              >
                {t.reset}
              </button>
            </div>
          </div>
        </section>
      </div>

      <p className="apps-cidr-planner-page__support">
        <a
          href={BUY_ME_A_COFFEE_URL}
          className="apps-cidr-planner-page__support-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.buyMeACoffee}
        </a>
      </p>

      {mobileSettingsOpen && (
        <CidrSheet
          title={t.mobileSettings}
          closeLabel={t.close}
          onClose={() => setMobileSettingsOpen(false)}
        >
          <div className="apps-cidr-planner-settings">
            <label className="cidr-sheet__field">
              <span className="cidr-sheet__label">{t.parent}</span>
              <input
                className="cidr-sheet__input"
                value={parentInput}
                onChange={(event) => setParentInput(event.target.value)}
                onBlur={handleParentBlur}
                inputMode="decimal"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </label>
            <div className="apps-cidr-planner-settings__mode">
              <ModeToggle mode={state.ipCountMode} onChange={handleModeChange} lang={lang} />
            </div>
            {parentError && <p className="cidr-sheet__error">{parentError}</p>}
            <div className="apps-cidr-planner-settings__danger">
              <button
                type="button"
                className="apps-cidr-planner-settings__reset-btn"
                onClick={() => {
                  setMobileSettingsOpen(false);
                  handleReset();
                }}
              >
                {t.reset}
              </button>
            </div>
          </div>
          <div className="cidr-sheet__actions">
            <button
              type="button"
              className="cidr-sheet__btn cidr-sheet__btn--primary"
              onClick={() => setMobileSettingsOpen(false)}
            >
              {t.close}
            </button>
          </div>
        </CidrSheet>
      )}

      {dialog?.type === 'gap' && (
        <GapActionDialog
          gap={dialog.gap}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onAddOne={() => setDialog({ type: 'gap-add', gap: dialog.gap })}
          onSplit={() => setDialog({ type: 'split', gap: dialog.gap })}
        />
      )}

      {dialog?.type === 'gap-add' && (
        <AddCidrDialog
          key={`gap-add-${dialog.gap.start.toString()}`}
          parentCidr={state.parentCidr}
          existingCidrs={existingCidrs}
          ipCountMode={state.ipCountMode}
          lang={lang}
          gap={dialog.gap}
          initialCidr={getLargestCidrForGap(dialog.gap, state.ipCountMode) ?? ''}
          onClose={() => setDialog(null)}
          onConfirm={addBlock}
        />
      )}

      {dialog?.type === 'split' && (
        <SplitDialog
          gap={dialog.gap}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onConfirm={addBlocks}
        />
      )}

      {dialog?.type === 'add' && (
        <AddCidrDialog
          parentCidr={state.parentCidr}
          existingCidrs={existingCidrs}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onConfirm={addBlock}
        />
      )}

      {dialog?.type === 'import' && (
        <ImportCidrDialog
          parentCidr={state.parentCidr}
          initialText={state.blocksText ?? serializeBlocksToText(state.blocks)}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onConfirm={replaceBlocksFromText}
        />
      )}

      {dialog?.type === 'block' && (
        <BlockActionDialog
          block={dialog.block}
          existingCidrs={existingCidrs}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onEdit={() => setDialog({ type: 'block-edit', block: dialog.block })}
          onSplit={() => setDialog({ type: 'block-split', block: dialog.block })}
        />
      )}

      {dialog?.type === 'block-edit' && (
        <BlockDetailSheet
          block={dialog.block}
          parentCidr={state.parentCidr}
          existingCidrs={existingCidrs}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onSave={(payload) => updateBlock(dialog.block.id, payload)}
          onDelete={() => requestDeleteBlock(dialog.block, 'block-edit')}
        />
      )}

      {dialog?.type === 'block-split' && (
        <BlockSplitDialog
          block={dialog.block}
          ipCountMode={state.ipCountMode}
          lang={lang}
          onClose={() => setDialog(null)}
          onSplit={(payloads) => splitBlock(dialog.block.id, payloads)}
        />
      )}

      {dialog?.type === 'aws-mode-confirm' && (
        <AwsModeConfirmDialog
          blocksToRemove={dialog.blocksToRemove}
          lang={lang}
          onClose={() => setDialog(null)}
          onConfirm={() => applyIpCountMode('aws', dialog.nextBlocks)}
        />
      )}

      {dialog?.type === 'confirm-reset' && (
        <ConfirmDialog
          title={t.resetTitle}
          message={t.resetConfirm}
          cancelLabel={t.cancel}
          confirmLabel={t.confirmReset}
          closeLabel={t.close}
          danger
          onClose={() => setDialog(null)}
          onConfirm={performReset}
        />
      )}

      {dialog?.type === 'confirm-parent' && (
        <ConfirmDialog
          title={t.parentChangeTitle}
          message={t.parentConfirm}
          cancelLabel={t.cancel}
          confirmLabel={t.confirmParent}
          closeLabel={t.close}
          danger
          onClose={handleCancelParentChange}
          onConfirm={handleConfirmParentChange}
        />
      )}

      {dialog?.type === 'confirm-delete-block' && (
        <ConfirmDialog
          title={t.deleteBlockTitle}
          message={t.deleteBlockConfirm(dialog.block.cidr, dialog.block.name)}
          cancelLabel={t.cancel}
          confirmLabel={t.confirmDelete}
          closeLabel={t.close}
          danger
          onClose={handleCancelDeleteBlock}
          onConfirm={() => deleteBlock(dialog.block.id)}
        />
      )}
    </main>
  );
}

export default AppsCidrPlanner;
